/**
 * M11: shared battle scene for the replay (BattleView) and the live battle (LiveBattleView).
 *
 * It only DRAWS. All state comes from the caller, and every effect is triggered by a real battle event
 * (fire / shieldBreak / destroy) – the scene never invents hits, damage or results:
 *   - fire():    bolt or beam from attacker to target; on arrival an impact; a shield flash when the shield took damage
 *   - destroy(): explosion + the ship's wreck stays faint
 *   - setState(): silent state sync (seek / restart / initial state) – bars, damage look, wreck, no effects
 * Ships use the shared ship art (graphics/ship-art.ts) so the map, fleet menu and battle show the same silhouettes.
 * Effects are pooled sprites on baked textures; nothing is created per frame. The replay stays deterministic:
 * effect shapes derive only from ship ids and tick data (no Math.random).
 */
import * as PIXI from 'pixi.js';
import type { ReplayShip } from '@galaxis/shared';
import { ArtAtlas } from '../graphics/pixi-art.ts';
import { ART_BOX, designOf } from '../graphics/ship-art.ts';
import { cellHash } from '../graphics/lod.ts';
import { currentQuality, prefersReducedMotion, type Quality } from '../graphics/quality.ts';
import { FrameGuard } from '../graphics/frame-guard.ts';
import { smoothStep, type CameraTarget } from '../replay/timeline.ts';
import { ROLE_COLORS, type Role } from '../design/tokens.ts';
import { MapBackdrop } from './map-backdrop.ts';
import { audio } from '../audio/index.ts';

const SHIP_TEX_H = ART_BOX.h * 32;
const BAR_W = 38;
const BAR_H = 4;

const SIDE_ROLE: Record<ReplayShip['side'], Role> = { attacker: 'survey', defender: 'military' };
const SIDE_BEAM: Record<ReplayShip['side'], number> = { attacker: 0x7dd3fc, defender: 0xfb7185 };

/** On-screen length (px in the 640×360 arena) per class. */
const CLASS_PX: Record<string, number> = { scout: 24, construction: 30, corvette: 28, frigate: 38, cruiser: 52 };

interface SceneShip {
  ship: ReplayShip;
  container: PIXI.Container;
  art: PIXI.Sprite;
  engine: PIXI.Sprite;
  shieldBubble: PIXI.Sprite;
  bars: PIXI.Graphics;
  label: PIXI.Text;
  x: number; y: number; tx: number; ty: number;
  hull: number; shield: number; destroyed: boolean;
  damageTier: number;
  shieldFlash: number;
  hitFlash: number;
  px: number;
  lastTrail: number;
}

type FxKind = 'bolt' | 'impact' | 'shieldHit' | 'ring' | 'blast' | 'spark' | 'trail';
interface Fx {
  kind: FxKind;
  sprite: PIXI.Sprite;
  t0: number;
  dur: number;
  x0: number; y0: number; x1: number; y1: number;
  scale0: number; scale1: number;
  after?: () => void;
  /** last trail emission time (bolts) */
  lt?: number;
}

interface Beam { x0: number; y0: number; x1: number; y1: number; color: number; t0: number; dur: number; width: number }

export class BattleScene {
  quality: Quality;
  readonly reduced: boolean;
  /** Track 2: 1 = normal; the replay slows the scene for kill shots (effects and camera follow it). */
  timeScale = 1;
  /** Called when the frame-time guard lowered the quality on a slow device. */
  onQualityChange: ((q: Quality) => void) | null = null;
  private guard: FrameGuard;
  private camLayer = new PIXI.Container();
  private cam: CameraTarget = { x: 0, y: 0, zoom: 1 };
  private camTarget: CameraTarget | null = null;
  private atlas: ArtAtlas;
  private backdrop: MapBackdrop;
  private ships = new Map<string, SceneShip>();
  private shipLayer = new PIXI.Container();
  private fxLayer = new PIXI.Container();
  private beamGfx = new PIXI.Graphics();
  private fxs: Fx[] = [];
  private beams: Beam[] = [];
  private pool: PIXI.Sprite[] = [];
  private now = 0;
  private shake = 0;
  private root = new PIXI.Container();
  private destroyed = false;

  constructor(renderer: PIXI.Renderer, stage: PIXI.Container, private w: number, private h: number) {
    this.quality = currentQuality();
    this.guard = new FrameGuard(this.quality);
    this.reduced = prefersReducedMotion();
    this.cam = { x: w / 2, y: h / 2, zoom: 1 };
    this.atlas = new ArtAtlas(renderer, this.quality);
    this.backdrop = new MapBackdrop(renderer, this.atlas, this.quality, w, h);
    this.beamGfx.blendMode = 'add';
    this.camLayer.addChild(this.backdrop.screenLayer, this.backdrop.worldLayer, this.drawDivider(), this.shipLayer, this.beamGfx, this.fxLayer);
    this.root.addChild(this.camLayer);
    this.applyCamera();
    stage.addChild(this.root);
    this.backdrop.update({ x: 0, y: 0, scale: 1 }, w, h);
  }

  // ---------------------------------------------------------------- camera (Track 2)

  /** Where the auto-camera should look; null = the whole arena. The scene eases toward it every frame. */
  setCameraTarget(t: CameraTarget | null): void { this.camTarget = t; }

  /** Jump without easing (seek / restart). */
  resetCamera(): void {
    this.camTarget = null;
    this.cam = { x: this.w / 2, y: this.h / 2, zoom: 1 };
    this.applyCamera();
  }

  /** Current position of a ship in arena coordinates (camera focus). */
  shipPos(id: string): { x: number; y: number } | null {
    const s = this.ships.get(id);
    return s ? { x: s.x, y: s.y } : null;
  }

  private applyCamera(): void {
    this.camLayer.pivot.set(this.cam.x, this.cam.y);
    this.camLayer.position.set(this.w / 2, this.h / 2);
    this.camLayer.scale.set(this.cam.zoom);
  }

  private stepCamera(dtMs: number): void {
    const t = this.camTarget ?? { x: this.w / 2, y: this.h / 2, zoom: 1 };
    if (this.reduced && this.camTarget) { this.cam = { ...t }; this.applyCamera(); return; }
    const c = this.cam;
    if (Math.abs(c.x - t.x) < 0.02 && Math.abs(c.y - t.y) < 0.02 && Math.abs(c.zoom - t.zoom) < 0.0005) return;
    c.x = smoothStep(c.x, t.x, dtMs, 260); c.y = smoothStep(c.y, t.y, dtMs, 260); c.zoom = smoothStep(c.zoom, t.zoom, dtMs, 320);
    this.applyCamera();
  }

  /** The frame-time guard asked for a lighter look: fewer particles, no trails, no shake, no background drift. */
  private downgrade(q: Quality): void {
    this.quality = q;
    this.onQualityChange?.(q);
  }

  private drawDivider(): PIXI.Graphics {
    const g = new PIXI.Graphics();
    for (let y = 8; y < this.h; y += 12) g.moveTo(this.w / 2, y).lineTo(this.w / 2, y + 5);
    g.stroke({ color: 0x64748b, alpha: 0.22, width: 1 });
    return g;
  }

  // ---------------------------------------------------------------- ships

  addShip(ship: ReplayShip, x: number, y: number): void {
    const role = SIDE_ROLE[ship.side];
    const px = CLASS_PX[ship.shipClass] ?? 32;
    const container = new PIXI.Container();
    container.position.set(x, y);

    const art = new PIXI.Sprite(this.atlas.ship(ship.shipClass, role, 0));
    art.anchor.set(0.5);
    art.rotation = ship.side === 'attacker' ? Math.PI / 2 : -Math.PI / 2;
    art.scale.set(px / SHIP_TEX_H);

    const engine = new PIXI.Sprite(this.atlas.glow(64));
    engine.anchor.set(0.5);
    engine.tint = designOf(ship.shipClass).flame.color;
    engine.blendMode = 'add';
    engine.alpha = 0.7;
    engine.scale.set(px / 150, px / 230);
    engine.position.set((ship.side === 'attacker' ? -1 : 1) * px * 0.55, 0);

    const shieldBubble = new PIXI.Sprite(this.atlas.glow(64));
    shieldBubble.anchor.set(0.5);
    shieldBubble.tint = 0x67e8f9;
    shieldBubble.blendMode = 'add';
    shieldBubble.alpha = 0;
    shieldBubble.scale.set((px * 2.2) / 128);

    const bars = new PIXI.Graphics();
    bars.position.set(0, -px * 0.62 - 12);

    const label = new PIXI.Text({
      text: ship.name.length > 10 ? ship.name.slice(0, 10) : ship.name,
      style: { fontSize: 9, fill: 0x94a3b8, fontFamily: 'monospace', stroke: { color: 0x030712, width: 2, join: 'round' } },
      resolution: 2,
    });
    label.anchor.set(0.5, 0);
    label.position.set(0, px * 0.55 + 4);

    container.addChild(engine, shieldBubble, art, bars, label);
    this.shipLayer.addChild(container);

    const s: SceneShip = { ship, container, art, engine, shieldBubble, bars, label, x, y, tx: x, ty: y, hull: ship.maxHull, shield: ship.maxShield, destroyed: false, damageTier: 0, shieldFlash: 0, hitFlash: 0, px, lastTrail: 0 };
    this.ships.set(ship.id, s);
    this.drawBars(s);
  }

  /** Authoritative position (live battle): the ship eases toward it. */
  moveTo(id: string, x: number, y: number): void {
    const s = this.ships.get(id);
    if (s) { s.tx = x; s.ty = y; }
  }

  /** Silent state sync – no effects (seek, restart, initial state). */
  setState(id: string, hull: number, shield: number, destroyed: boolean): void {
    const s = this.ships.get(id);
    if (!s) return;
    s.hull = Math.max(0, hull); s.shield = Math.max(0, shield); s.destroyed = destroyed;
    this.refreshShip(s);
  }

  private refreshShip(s: SceneShip): void {
    const dmg = s.ship.maxHull > 0 ? 1 - s.hull / s.ship.maxHull : 0;
    const tier = dmg > 0.66 ? 3 : dmg > 0.33 ? 2 : dmg > 0.05 ? 1 : 0;
    if (tier !== s.damageTier) {
      s.damageTier = tier;
      s.art.texture = this.atlas.ship(s.ship.shipClass, SIDE_ROLE[s.ship.side], [0, 0.2, 0.5, 0.85][tier]!);
    }
    if (s.destroyed) {
      s.art.alpha = 0.16; s.art.tint = 0x64748b;
      s.engine.visible = false; s.bars.visible = false; s.label.alpha = 0.3; s.shieldBubble.alpha = 0;
    } else {
      s.art.alpha = 1; s.art.tint = 0xffffff; s.engine.visible = true; s.bars.visible = true; s.label.alpha = 1;
    }
    this.drawBars(s);
  }

  private drawBars(s: SceneShip): void {
    const g = s.bars;
    g.clear();
    const shieldRatio = s.ship.maxShield > 0 ? Math.max(0, s.shield / s.ship.maxShield) : 0;
    const hullRatio = s.ship.maxHull > 0 ? Math.max(0, s.hull / s.ship.maxHull) : 0;
    // shield on top, hull below; both with a dark frame so they stay readable on nebulae
    g.roundRect(-BAR_W / 2 - 1, -1, BAR_W + 2, BAR_H * 2 + 4, 2).fill({ color: 0x030712, alpha: 0.75 });
    g.roundRect(-BAR_W / 2, 0, BAR_W, BAR_H, 1.5).fill({ color: 0x1e293b });
    if (shieldRatio > 0) g.roundRect(-BAR_W / 2, 0, BAR_W * shieldRatio, BAR_H, 1.5).fill({ color: 0x38bdf8 });
    g.roundRect(-BAR_W / 2, BAR_H + 2, BAR_W, BAR_H, 1.5).fill({ color: 0x1e293b });
    if (hullRatio > 0) g.roundRect(-BAR_W / 2, BAR_H + 2, BAR_W * hullRatio, BAR_H, 1.5).fill({ color: hullRatio > 0.5 ? 0x4ade80 : hullRatio > 0.25 ? 0xfbbf24 : 0xf87171 });
  }

  // ---------------------------------------------------------------- real events

  /** A real attack event: bolt/beam from attacker to target, then impact (+ shield flash). Also updates the numbers. */
  fire(attackerId: string | undefined, targetId: string, shieldDamage: number, hullDamage: number): void {
    const t = this.ships.get(targetId);
    if (!t) return;
    const a = attackerId ? this.ships.get(attackerId) : undefined;
    t.shield = Math.max(0, t.shield - shieldDamage);
    t.hull = Math.max(0, t.hull - hullDamage);
    const sx = a ? a.x : (t.ship.side === 'attacker' ? this.w : 0), sy = a ? a.y : t.y;
    const color = a ? SIDE_BEAM[a.ship.side] : 0xffffff;
    const cls = a?.ship.shipClass ?? 'corvette';
    const travel = cls === 'cruiser' ? 90 : cls === 'frigate' ? 140 : 190;
    audio.play(cls === 'cruiser' || cls === 'frigate' ? 'beam' : 'laser', { pan: this.panOf(sx), vol: 0.8 });
    const onHit = () => {
      this.refreshShip(t);
      if (shieldDamage > 0) { t.shieldFlash = 1; audio.play('shieldHit', { pan: this.panOf(t.x) }); }
      if (hullDamage > 0 || shieldDamage <= 0) { t.hitFlash = 1; this.spawnImpact(t.x, t.y, color, hullDamage > 0); }
      else this.spawnImpact(t.x, t.y, 0x67e8f9, false);
    };
    if (cls === 'cruiser' || cls === 'frigate') {
      this.beams.push({ x0: sx, y0: sy, x1: t.x, y1: t.y, color, t0: this.now, dur: travel + 40, width: cls === 'cruiser' ? 1.7 : 1.2 });
      this.later(travel * 0.5, onHit);
    } else {
      const sp = this.takeSprite(this.atlas.glow(64), color, 0.95);
      const ang = Math.atan2(t.y - sy, t.x - sx);
      sp.rotation = ang;
      this.fxs.push({ kind: 'bolt', sprite: sp, t0: this.now, dur: travel, x0: sx, y0: sy, x1: t.x, y1: t.y, scale0: 0.3, scale1: 0.3, after: onHit });
    }
  }

  /** A real shield_break event. */
  shieldBreak(targetId: string): void {
    const t = this.ships.get(targetId);
    if (!t) return;
    t.shield = 0;
    t.shieldFlash = 1.4;
    audio.play('shieldBreak', { pan: this.panOf(t.x) });
    this.ringAt(t.x, t.y, 0x67e8f9, t.px * 1.6, 380);
    this.drawBars(t);
  }

  /** A real ship_destroyed event: explosion; the wreck stays faint. */
  destroy(targetId: string): void {
    const t = this.ships.get(targetId);
    if (!t) return;
    t.hull = 0; t.destroyed = true;
    this.later(120, () => {
      audio.play('explosion', { pan: this.panOf(t.x), vol: Math.min(1.3, t.px / 36) });
      this.refreshShip(t);
      this.blast(t);
    });
  }

  private blast(t: SceneShip): void {
    const size = t.px;
    const core = this.takeSprite(this.atlas.glow(64), 0xfff1c2, 1);
    core.position.set(t.x, t.y);
    this.fxs.push({ kind: 'blast', sprite: core, t0: this.now, dur: 520, x0: t.x, y0: t.y, x1: t.x, y1: t.y, scale0: 0.2, scale1: (size * 3) / 128 });
    const fire = this.takeSprite(this.atlas.glow(64), 0xff7a45, 0.8);
    fire.position.set(t.x, t.y);
    this.fxs.push({ kind: 'blast', sprite: fire, t0: this.now, dur: 760, x0: t.x, y0: t.y, x1: t.x, y1: t.y, scale0: 0.1, scale1: (size * 4.4) / 128 });
    this.ringAt(t.x, t.y, 0xffd9a0, size * 3.2, 640);
    if (!this.reduced && this.quality !== 'low') {
      this.shake = 1;
      const n = this.quality === 'high' ? 9 : 6;
      for (let i = 0; i < n; i++) {
        // deterministic fragments: derived from the ship id + index only
        const hsh = cellHash(i, this.hashOf(t.ship.id), 7);
        const ang = (i / n) * Math.PI * 2 + hsh;
        const dist = size * (0.8 + cellHash(i, this.hashOf(t.ship.id), 9) * 1.4);
        const sp = this.takeSprite(this.atlas.glow(64), i % 2 ? 0xffb347 : 0xe2e8f0, 0.9);
        this.fxs.push({ kind: 'spark', sprite: sp, t0: this.now, dur: 620 + (i % 3) * 120, x0: t.x, y0: t.y, x1: t.x + Math.cos(ang) * dist, y1: t.y + Math.sin(ang) * dist, scale0: 0.1, scale1: 0.02 });
      }
    }
  }

  private panOf(x: number): number { return Math.max(-0.8, Math.min(0.8, (x / this.w - 0.5) * 1.6)); }

  private hashOf(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (Math.imul(h, 31) + id.charCodeAt(i)) | 0;
    return h;
  }

  private spawnImpact(x: number, y: number, color: number, hull: boolean): void {
    const sp = this.takeSprite(this.atlas.glow(64), color, 1);
    sp.position.set(x, y);
    this.fxs.push({ kind: 'impact', sprite: sp, t0: this.now, dur: hull ? 320 : 240, x0: x, y0: y, x1: x, y1: y, scale0: 0.1, scale1: hull ? 0.55 : 0.4 });
    if (hull && !this.reduced) {
      for (let i = 0; i < 3; i++) {
        const ang = cellHash(i, Math.round(x * 3 + y), 11) * Math.PI * 2;
        const sparkSp = this.takeSprite(this.atlas.glow(64), 0xffd19a, 0.9);
        this.fxs.push({ kind: 'spark', sprite: sparkSp, t0: this.now, dur: 300, x0: x, y0: y, x1: x + Math.cos(ang) * 16, y1: y + Math.sin(ang) * 16, scale0: 0.06, scale1: 0.01 });
      }
    }
  }

  /** Soft fading glow dropped behind a moving bolt / ship (pooled sprite; skipped on low quality and reduced motion). */
  private emitTrail(x: number, y: number, color: number, scale0: number, scale1: number, dur: number): void {
    const sp = this.takeSprite(this.atlas.glow(64), color, 0.45);
    sp.position.set(x, y);
    this.fxs.push({ kind: 'trail', sprite: sp, t0: this.now, dur, x0: x, y0: y, x1: x, y1: y, scale0, scale1 });
  }

  private ringAt(x: number, y: number, color: number, radiusPx: number, dur: number): void {
    const sp = this.takeSprite(this.atlas.ring(), color, 0.9);
    sp.position.set(x, y);
    this.fxs.push({ kind: 'ring', sprite: sp, t0: this.now, dur, x0: x, y0: y, x1: x, y1: y, scale0: 0.1, scale1: radiusPx / 62 });
  }

  private timers: Array<{ at: number; fn: () => void }> = [];
  private later(ms: number, fn: () => void): void { this.timers.push({ at: this.now + ms, fn }); }

  private takeSprite(tex: PIXI.Texture, tint: number, alpha: number): PIXI.Sprite {
    const sp = this.pool.pop() ?? (() => { const s = new PIXI.Sprite(); s.anchor.set(0.5); s.blendMode = 'add'; return s; })();
    sp.texture = tex; sp.tint = tint; sp.alpha = alpha; sp.rotation = 0; sp.visible = true;
    this.fxLayer.addChild(sp);
    return sp;
  }

  private release(sp: PIXI.Sprite): void {
    sp.visible = false; this.fxLayer.removeChild(sp); this.pool.push(sp);
  }

  /** True while an effect still animates (the owner may stop rendering when this is false and nothing moves). */
  get busy(): boolean { return this.fxs.length > 0 || this.beams.length > 0 || this.timers.length > 0; }

  /** Drops all running effects (seek/restart). */
  clearEffects(): void {
    for (const f of this.fxs) this.release(f.sprite);
    this.fxs.length = 0; this.beams.length = 0; this.timers.length = 0;
    this.beamGfx.clear();
    this.root.position.set(0, 0);
    for (const s of this.ships.values()) { s.shieldFlash = 0; s.hitFlash = 0; s.shieldBubble.alpha = 0; s.art.tint = s.destroyed ? 0x64748b : 0xffffff; }
  }

  // ---------------------------------------------------------------- frame

  /** `dtMs`: time since the last call. Call once per rendered frame. */
  update(realDtMs: number): void {
    if (this.destroyed) return;
    const lowered = this.guard.push(realDtMs);
    if (lowered) this.downgrade(lowered);
    const dtMs = realDtMs * this.timeScale;
    this.stepCamera(realDtMs);
    this.now += dtMs;
    const now = this.now;
    // background drift: far layers slower than near ones (depth)
    if (!this.reduced && this.quality !== 'low') this.backdrop.update({ x: -now * 0.006, y: 0, scale: 1 }, this.w, this.h);

    // ship easing + shield / hit flashes + engine flicker
    const ease = 1 - Math.pow(0.85, dtMs / 16.7);
    for (const s of this.ships.values()) {
      if (s.x !== s.tx || s.y !== s.ty) {
        s.x += (s.tx - s.x) * ease; s.y += (s.ty - s.y) * ease;
        if (Math.abs(s.tx - s.x) < 0.05) s.x = s.tx;
        if (Math.abs(s.ty - s.y) < 0.05) s.y = s.ty;
        s.container.position.set(s.x, s.y);
        if (!this.reduced && this.quality !== 'low' && !s.destroyed && this.now - s.lastTrail > 45) {
          s.lastTrail = this.now;
          this.emitTrail(s.x + (s.ship.side === 'attacker' ? -s.px * 0.5 : s.px * 0.5), s.y, designOf(s.ship.shipClass).flame.color, s.px / 260, s.px / 900, 420);
        }
      }
      if (s.shieldFlash > 0) {
        s.shieldFlash = Math.max(0, s.shieldFlash - dtMs / 260);
        s.shieldBubble.alpha = Math.min(0.9, s.shieldFlash * 0.7);
        if (s.shieldFlash === 0) s.shieldBubble.alpha = s.shield > 0 && !s.destroyed ? 0.04 : 0;
      }
      if (s.hitFlash > 0) {
        s.hitFlash = Math.max(0, s.hitFlash - dtMs / 160);
        s.art.tint = s.destroyed ? 0x64748b : s.hitFlash > 0 ? 0xffe2d0 : 0xffffff;
      }
      if (!s.destroyed && !this.reduced) s.engine.alpha = 0.55 + 0.25 * Math.sin(now / 70 + s.px);
    }

    // timers
    if (this.timers.length) {
      const due = this.timers.filter(t => t.at <= now);
      if (due.length) { this.timers = this.timers.filter(t => t.at > now); for (const t of due) t.fn(); }
    }

    // beams
    this.beamGfx.clear();
    if (this.beams.length) {
      const keep: Beam[] = [];
      for (const b of this.beams) {
        const p = (now - b.t0) / b.dur;
        if (p >= 1) continue;
        keep.push(b);
        const a = p < 0.35 ? p / 0.35 : 1 - (p - 0.35) / 0.65;
        this.beamGfx.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1);
        this.beamGfx.stroke({ color: b.color, alpha: 0.2 * a, width: b.width * 3 });
        this.beamGfx.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1);
        this.beamGfx.stroke({ color: 0xffffff, alpha: 0.75 * a, width: b.width * 0.6 });
      }
      this.beams = keep;
    }

    // sprite effects
    if (this.fxs.length) {
      const done: Fx[] = [];
      const count = this.fxs.length; // trails spawned this frame start next frame
      for (let i = 0; i < count; i++) {
        const f = this.fxs[i]!;
        const p = Math.min(1, (now - f.t0) / f.dur);
        const e = f.kind === 'bolt' ? p : 1 - (1 - p) * (1 - p);
        f.sprite.position.set(f.x0 + (f.x1 - f.x0) * e, f.y0 + (f.y1 - f.y0) * e);
        const k = f.scale0 + (f.scale1 - f.scale0) * e;
        if (f.kind === 'bolt') {
          f.sprite.scale.set(k, k * 0.3);
          if (!this.reduced && this.quality !== 'low' && now - (f.lt ?? 0) > 28) { f.lt = now; this.emitTrail(f.sprite.x, f.sprite.y, f.sprite.tint as number, 0.16, 0.05, 180); }
        }
        else f.sprite.scale.set(k);
        if (f.kind !== 'bolt') f.sprite.alpha = Math.max(0, (f.kind === 'ring' ? 0.9 : f.kind === 'trail' ? 0.45 : 1) * (1 - p) * (1 - p * 0.3));
        if (p >= 1) done.push(f);
      }
      if (done.length) {
        this.fxs = this.fxs.filter(f => !done.includes(f));
        for (const f of done) { this.release(f.sprite); f.after?.(); }
      }
    }

    // screen shake (explosions only; off with reduced motion)
    if (this.shake > 0) {
      this.shake = Math.max(0, this.shake - dtMs / 260);
      const m = this.shake * 3;
      this.root.position.set(Math.sin(now * 0.09) * m, Math.cos(now * 0.11) * m);
    } else if (this.root.x !== 0 || this.root.y !== 0) this.root.position.set(0, 0);
  }

  destroyScene(): void {
    this.destroyed = true;
    this.fxs.length = 0; this.beams.length = 0; this.timers.length = 0;
    try { this.backdrop.destroy(); this.atlas.destroy(); } catch { /* ignore */ }
    this.root.destroy({ children: true });
  }
}

export const BATTLE_ROLE_COLORS = { attacker: ROLE_COLORS.survey.hex, defender: ROLE_COLORS.military.hex };
